import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
} from "lucide-react";
import { Link } from "react-router";
import type { Order } from "../../../api/SolarCloudApi/models/Order";
import { Button } from "../../../components/ui/Button";
import { Card, CardContent } from "../../../components/ui/Card";
import { currencyFormat } from "../../../util/formats";
import {
  getDashboardPurchases,
  getKilowattsFromWatts,
  getOrderPurchaseAmount,
  getPanelsFromWatts,
} from "./bottomDashboardCards";

const purchasesPerPage = 4;
const maximumPageDots = 5;

type RecentPurchasesCardProps = {
  orders?: Order[];
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
};

export default function RecentPurchasesCard({
  orders,
  isLoading,
  error,
  onRetry,
}: RecentPurchasesCardProps) {
  const purchases = useMemo(() => getDashboardPurchases(orders), [orders]);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pageCount = Math.ceil((purchases?.length ?? 0) / purchasesPerPage);
  const pageDotCount = Math.min(pageCount, maximumPageDots);

  const updateCarouselState = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    setCanScrollPrevious(carousel.scrollLeft > 0.5);
    setCanScrollNext(
      carousel.scrollLeft + carousel.clientWidth < carousel.scrollWidth - 0.5
    );
    setCurrentPage(
      carousel.clientWidth > 0
        ? Math.round(carousel.scrollLeft / carousel.clientWidth)
        : 0
    );
  }, []);

  useEffect(() => {
    updateCarouselState();
    window.addEventListener("resize", updateCarouselState);
    return () => window.removeEventListener("resize", updateCarouselState);
  }, [purchases?.length, updateCarouselState]);

  const scrollCarousel = (direction: -1 | 1) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.scrollBy({
      left: direction * carousel.clientWidth,
      behavior: "smooth",
    });
  };

  const scrollCarouselToPage = (page: number) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.scrollTo({
      left: page * carousel.clientWidth,
      behavior: "smooth",
    });
  };

  return (
    <Card
      aria-busy={isLoading}
      className="h-full rounded-[20px] border-[#232836] bg-[#12151C] py-0 text-[#D0D0D0] shadow-none"
    >
      <CardContent className="flex h-full flex-col p-2.5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold leading-5 text-[#D0D0D0]">
            Recent Purchases
          </h2>
          <Link
            className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-[#E9A837] hover:text-[#FFC64D]"
            to="/dashboard/orders"
          >
            View all <ArrowRight aria-hidden="true" className="size-3" />
          </Link>
        </div>

        {isLoading && orders === undefined ? (
          <div
            className="flex min-h-[92px] items-center justify-center gap-2 text-xs text-[#9CA3AF]"
            role="status"
          >
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
            Loading purchases…
          </div>
        ) : error && purchases === undefined ? (
          <div className="flex min-h-[92px] flex-col items-center justify-center gap-2 text-center" role="alert">
            <p className="text-xs text-[#9CA3AF]">
              Unable to load your purchases.
            </p>
            {onRetry && (
              <Button
                className="rounded-lg border border-[#2F3648] px-2 py-1 text-[10px] text-[#D0D0D0] hover:bg-[#181C25]"
                onClick={onRetry}
                type="button"
                variant="outline"
              >
                Retry
              </Button>
            )}
          </div>
        ) : purchases?.length === 0 ? (
          <div className="flex min-h-[92px] items-center justify-center px-4 text-center text-xs text-[#6B7280]">
            No purchases to show yet.
          </div>
        ) : purchases === undefined ? (
          <div className="flex min-h-[92px] items-center justify-center px-4 text-center text-xs text-[#9CA3AF]" role="status">
            Purchase details are unavailable.
          </div>
        ) : (
          <>
            {error && (
              <p className="mt-1 text-[10px] text-amber-300" role="status">
                Showing the last loaded purchases; the latest refresh failed.
              </p>
            )}
            <div
              aria-label="Recent SolarCloud purchases"
              className="mt-auto flex min-w-0 items-center gap-1 overscroll-x-contain pt-2"
            >
              <CarouselButton
                ariaLabel="Show previous purchases"
                disabled={!canScrollPrevious}
                onClick={() => scrollCarousel(-1)}
              >
                <ChevronLeft aria-hidden="true" className="size-3.5" />
              </CarouselButton>
              <div
                aria-label="Recent SolarCloud purchases"
                className="flex min-w-0 flex-1 snap-x snap-mandatory gap-1.5 overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                onScroll={updateCarouselState}
                ref={carouselRef}
                tabIndex={0}
              >
                {purchases.map((purchase, index) => (
                  <PurchaseSlide
                    isSelected={index === currentPage * purchasesPerPage}
                    key={purchase.uuid ?? `${purchase.created ?? "purchase"}-${index}`}
                    order={purchase}
                  />
                ))}
              </div>
              <CarouselButton
                ariaLabel="Show next purchases"
                disabled={!canScrollNext}
                onClick={() => scrollCarousel(1)}
              >
                <ChevronRight aria-hidden="true" className="size-3.5" />
              </CarouselButton>
            </div>
            {pageDotCount > 1 && (
              <div
                aria-label="Purchase pages"
                className="mt-2 flex items-center justify-center gap-2"
                role="group"
              >
                {Array.from({ length: pageDotCount }, (_, index) => (
                  <button
                    aria-current={Math.min(currentPage, pageDotCount - 1) === index}
                    aria-label={`Show purchase page ${index + 1}`}
                    className={`size-1.5 rounded-full ${Math.min(currentPage, pageDotCount - 1) === index ? "bg-[#FFC32E]" : "bg-[#344252]"}`}
                    key={index}
                    onClick={() => scrollCarouselToPage(index)}
                    type="button"
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PurchaseSlide({
  order,
  isSelected,
}: {
  order: Order;
  isSelected: boolean;
}) {
  const capacityKw = getKilowattsFromWatts(order.totalCapacityWatts);
  const panelCount = getPanelsFromWatts(order.totalCapacityWatts);
  const amount = getOrderPurchaseAmount(order);
  const parsedDate = order.created ? new Date(order.created) : undefined;
  const purchaseDate =
    parsedDate && Number.isFinite(parsedDate.getTime())
      ? new Intl.DateTimeFormat("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(parsedDate)
      : "—";
  const formattedAmount =
    currencyFormat(amount, {
      fractionalDigits: 2,
      excludeCurrencyId: true,
    }) ?? "—";
  const displayAmount = amount === undefined ? formattedAmount : `A${formattedAmount}`;

  return (
    <article className="min-w-0 basis-[calc((100%-18px)/4)] shrink-0 snap-start">
      <div
        className={`flex h-[90px] min-w-0 flex-col items-center justify-center rounded-[11px] border px-1.5 py-2 text-center ${isSelected ? "border-[#F3B712] bg-[#121C25]" : "border-[#1C2B39] bg-[#101923]"}`}
      >
        <p className="w-full truncate text-[9px] leading-3 text-[#8E9CAC]">
          {purchaseDate}
        </p>
        <p className="mt-1 truncate text-sm font-bold leading-4 text-[#E3E8EF]">
          {formatKilowatts(capacityKw)}
        </p>
        <p className="mt-0.5 truncate text-[10px] leading-3 text-[#AAB5C2]">
          {formatCount(panelCount)} panels
        </p>
        <p className="mt-1 truncate text-[10px] font-medium leading-3 text-[#D6DCE4]">
          {displayAmount}
        </p>
      </div>
    </article>
  );
}

function CarouselButton({
  ariaLabel,
  children,
  disabled,
  onClick,
}: {
  ariaLabel: string;
  children: ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={ariaLabel}
      className="size-5 shrink-0 rounded-full border border-[#2F3648] bg-[#161922] p-0 text-[#9CA3AF] hover:bg-[#232836] disabled:cursor-default disabled:opacity-30"
      disabled={disabled}
      onClick={onClick}
      size="icon"
      type="button"
      variant="outline"
    >
      {children}
    </Button>
  );
}

function formatKilowatts(value: number | undefined): string {
  if (value === undefined) return "—";

  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} kW`;
}

function formatCount(value: number | undefined): string {
  return value === undefined ? "—" : new Intl.NumberFormat().format(value);
}
